import { SafetyRoundSkeletonComponent } from './safety-rounds/safety-round-skeleton.component';
import { NgModule } from '@angular/core';
import { MenuModule } from 'primeng/menu';
import { FormsModule } from '@angular/forms';
import { FlexiComponent } from './components/flexi.component';
import { EcplTabNavigationModule } from 'src/app/shared/ecpl-tab-navigation';
import { RouterModule } from '@angular/router';
import { OHSPlanComponent } from './components/ohs-plan.component';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { MyHSEAdvisor } from './components/my-hse-advisor.component';
import {
  FlexiResolver,
  OHSDataResolver,
} from './services/flexi.resolver.service';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { AppSharedModule } from '../shared';
import { OnboardingContainerComponent } from './onboarding/onboarding-container.component';
import { OnboardingListComponent } from './onboarding/onboarding-list.component';
import { OnboardingWizardComponent } from './onboarding/onboarding-wizard.component';
import { OnboardingFinalViewComponent } from './onboarding/onboarding-final-view.component';
import { AdditionalResourcesComponent } from './onboarding/additional-resources.component';
import { BookingPortalFormComponent } from './booking-portal/booking-portal-form.component';
import {
  FlexiOnboardingResolver,
  FlexiOnboardingService,
} from './onboarding/flexi-onboarding.service';
import { ComplianceContainerComponent } from './compliance/compliance-container.component';
import { ComplianceListComponent } from './compliance/compliance-list.component';
import { ComplianceRatingComponent } from './compliance/compliance-rating.component';
import { ComplianceFinalViewComponent } from './compliance/compliance-final-view.component';
import {
  FlexiComplianceResolver,
  FlexiComplianceService,
} from './compliance/flexi-compliance.service';
import { TemplatesListComponent } from './compliance/templates/templates-list.component';
import { TemplatesEditorComponent } from './compliance/templates/templates-editor.component';
import { TemplateWizardComponent } from './compliance/templates/template-wizard.component';
import { FlexiComplianceTemplateService } from './compliance/templates/flexi-compliance-template.service';
import { ActionPointsListComponent } from './compliance/action-points-list.component';
import { FlexiComplianceHistoryStripComponent } from './compliance/history-strip.component';
import { CustomerRequestFormComponent } from './customer-request/customer-request-form.component';
import { FlexiAdvisorSettingsComponent } from './advisor-settings/flexi-advisor-settings.component';
import { FlexiAdvisorEditDialogComponent } from './advisor-settings/advisor-edit-dialog.component';
import { FlexiAdvisorBulkDialogComponent } from './advisor-settings/advisor-bulk-dialog.component';
import { SafetyRoundWorkspaceComponent } from './safety-rounds/safety-round-workspace.component';
import { SafetyRoundDeviationsComponent } from './safety-rounds/safety-round-deviations.component';
import { SafetyRoundListComponent } from './safety-rounds/safety-round-list.component';
import { SafetyRoundComponent } from './safety-rounds/safety-round.component';
import { SafetyRoundTemplateLibraryComponent } from './safety-rounds/templates/safety-round-template-library.component';
import { SafetyRoundTemplateEditorComponent } from './safety-rounds/templates/safety-round-template-editor.component';
import { SafetyRoundAssignDrawerComponent } from './safety-rounds/templates/safety-round-assign-drawer.component';
import { FlexiSettingsNavComponent } from './safety-rounds/templates/flexi-settings-nav.component';
import { ExposureRegisterOverviewComponent } from './exposure-register/exposure-register-overview.component';
import { ExposureAssessmentComponent } from './exposure-register/exposure-assessment.component';
import { ExposureCompleteDialogComponent } from './exposure-register/exposure-complete-dialog.component';
import { ExposureTemplateLibraryComponent } from './exposure-register/templates/exposure-template-library.component';
import { ExposureTemplateEditorComponent } from './exposure-register/templates/exposure-template-editor.component';
import { ExposureAssignDrawerComponent } from './exposure-register/templates/exposure-assign-drawer.component';
// Risk Analysis runs inside Flexi as a tab, reusing the Follow-ups components
// and resolvers rather than re-declaring them. FollowUpsModule exports
// RiskAnalysisComponent / RiskAnalysisListComponent / NewRiskAnalysisComponent /
// NewRiskPortalComponent, which is what makes them usable from flexi.routing.ts.
import { FollowUpsModule } from '../follow-ups/followups.module';
import {
  ApprovalNoticesResolver,
  FollowupDepartmentForAdminResolver,
  RiskAnalysisAdminResolver,
  RiskAnalysisCategoryResolver,
  RiskAnalysisEditResolver,
  RiskAnalysisListResolver,
  RiskAnalysisLogResolver,
  RiskAnalysisTopicCachedResolver,
  RiskAnalysisTopicForAdminResolver,
  UserForAdminResolver,
} from '../follow-ups/services/risk-analysis.resolver.service';
import { AllDepartmentResolver } from '../follow-ups/services/deviation.resolver.service';
import { DepartmentCachedListResolver } from '../follow-ups/services/activities.resolver.service';
import {
  BusinessCacheResolver,
  CompanyNameResolver,
  RiskListResolver,
} from '../businesses/services/business.resolver.service';
// Role-aware resolver — see the note in flexi.routing.ts.
import { EmployeeContactUserResolver } from '../users/services/user.resolver.service';

@NgModule({
  imports: [
    SafetyRoundSkeletonComponent,
    EcplTabNavigationModule,
    RouterModule,
    BrowserModule,
    CKEditorModule,
    TranslatePipe, TranslateDirective,
    CommonModule,
    FormsModule,
    AppSharedModule,
    // Row-action kebab popups. Not re-exported by AppSharedModule, so it is
    // imported per feature module — same as sds.module.ts does.
    MenuModule,
    FollowUpsModule,
  ],
  declarations: [
    FlexiComponent,
    OHSPlanComponent,
    MyHSEAdvisor,
    OnboardingContainerComponent,
    OnboardingListComponent,
    OnboardingWizardComponent,
    OnboardingFinalViewComponent,
    AdditionalResourcesComponent,
    BookingPortalFormComponent,
    ComplianceContainerComponent,
    ComplianceListComponent,
    ComplianceRatingComponent,
    ComplianceFinalViewComponent,
    TemplatesListComponent,
    TemplatesEditorComponent,
    TemplateWizardComponent,
    ActionPointsListComponent,
    FlexiComplianceHistoryStripComponent,
    CustomerRequestFormComponent,
    FlexiAdvisorSettingsComponent,
    FlexiAdvisorEditDialogComponent,
    FlexiAdvisorBulkDialogComponent,
    SafetyRoundListComponent,
    SafetyRoundWorkspaceComponent,
    SafetyRoundDeviationsComponent,
    SafetyRoundComponent,
    SafetyRoundTemplateLibraryComponent,
    SafetyRoundTemplateEditorComponent,
    SafetyRoundAssignDrawerComponent,
    FlexiSettingsNavComponent,
    ExposureRegisterOverviewComponent,
    ExposureAssessmentComponent,
    ExposureCompleteDialogComponent,
    ExposureTemplateLibraryComponent,
    ExposureTemplateEditorComponent,
    ExposureAssignDrawerComponent,
  ],
  providers: [
    FlexiResolver,
    OHSDataResolver,
    FlexiOnboardingService,
    FlexiOnboardingResolver,
    FlexiComplianceService,
    FlexiComplianceResolver,
    FlexiComplianceTemplateService,
    // Risk Analysis route resolvers — see flexi.routing.ts.
    ApprovalNoticesResolver,
    RiskAnalysisEditResolver,
    RiskAnalysisListResolver,
    RiskAnalysisAdminResolver,
    RiskAnalysisCategoryResolver,
    RiskAnalysisLogResolver,
    RiskAnalysisTopicCachedResolver,
    RiskAnalysisTopicForAdminResolver,
    FollowupDepartmentForAdminResolver,
    UserForAdminResolver,
    AllDepartmentResolver,
    DepartmentCachedListResolver,
    BusinessCacheResolver,
    CompanyNameResolver,
    EmployeeContactUserResolver,
    RiskListResolver,
  ],
  bootstrap: [],
})
export class FlexiModule {}
